import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin, Phone, CheckCircle, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const serviceAreas = {
  pennsylvania: {
    state: 'Pennsylvania',
    counties: [
      {
        name: 'Montgomery County',
        cities: ['Norristown', 'King of Prussia', 'Lansdale', 'Pottstown', 'Conshohocken', 'Ambler', 'Collegeville', 'Blue Bell'],
      },
      {
        name: 'Bucks County',
        cities: ['Doylestown', 'Levittown', 'Newtown', 'Bristol', 'Quakertown', 'Warminster', 'Langhorne', 'Yardley'],
      },
      {
        name: 'Philadelphia County',
        cities: ['Center City', 'Northeast Philly', 'South Philly', 'West Philly', 'Manayunk', 'Chestnut Hill', 'Germantown', 'Fishtown'],
      },
      {
        name: 'Delaware County',
        cities: ['Media', 'Chester', 'Springfield', 'Havertown', 'Drexel Hill', 'Upper Darby', 'Swarthmore', 'Broomall'],
      },
    ],
  },
  newJersey: {
    state: 'New Jersey',
    counties: [
      {
        name: 'Gloucester County',
        cities: ['Woodbury', 'Deptford', 'Washington Township', 'Glassboro', 'Pitman', 'Mullica Hill', 'Sewell'],
      },
      {
        name: 'North Burlington County',
        cities: ['Mount Holly', 'Bordentown', 'Burlington City', 'Willingboro', 'Delran', 'Moorestown', 'Maple Shade'],
      },
      {
        name: 'Camden County',
        cities: ['Camden', 'Cherry Hill', 'Haddonfield', 'Collingswood', 'Voorhees', 'Pennsauken', 'Gloucester City'],
      },
    ],
  },
};

const ServiceAreasPage = () => {
  return (
    <main data-testid="service-areas-page" className="pt-24 pb-20">
      <SEO 
        title="Appliance Repair Service Areas | Philadelphia, PA & NJ | BBM Appliances"
        description="BBM Appliances serves Philadelphia, Montgomery County, Bucks County, Delaware County PA and Gloucester, Burlington, Camden County NJ. Same-day appliance repair in 15+ locations. Call (855) 605-6034!"
        canonical="https://www.bbmappliances.com/service-areas"
        keywords="appliance repair Philadelphia, appliance repair Montgomery County PA, appliance repair Bucks County, appliance repair NJ, appliance repair Camden County, appliance repair near me, local appliance repair"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            <MapPin className="w-4 h-4 inline mr-1" />
            Service Coverage
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Areas We Serve
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            BBM Appliances operates over 15 service locations across Pennsylvania and New Jersey. 
            Our technicians are always nearby and ready to help.
          </p>
        </div>

        {/* Pennsylvania Section */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <span className="text-primary font-bold">PA</span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground">Pennsylvania</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {serviceAreas.pennsylvania.counties.map((county) => (
              <div
                key={county.name}
                data-testid={`county-${county.name.toLowerCase().replace(/ /g, '-')}`}
                className="bg-white rounded-2xl border border-border p-6 hover:shadow-lg transition-shadow"
              >
                <h3 className="font-heading font-semibold text-xl text-foreground mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  {county.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {county.cities.map((city) => (
                    <span
                      key={city}
                      className="px-3 py-1 bg-secondary text-sm text-foreground rounded-full"
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* New Jersey Section */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <span className="text-primary font-bold">NJ</span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground">New Jersey</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceAreas.newJersey.counties.map((county) => (
              <div
                key={county.name}
                data-testid={`county-${county.name.toLowerCase().replace(/ /g, '-')}`}
                className="bg-white rounded-2xl border border-border p-6 hover:shadow-lg transition-shadow"
              >
                <h3 className="font-heading font-semibold text-xl text-foreground mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  {county.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {county.cities.map((city) => (
                    <span
                      key={city}
                      className="px-3 py-1 bg-secondary text-sm text-foreground rounded-full"
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Service Info */}
        <div className="bg-secondary/50 rounded-3xl p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-heading text-2xl font-bold text-foreground mb-4">
                Don't See Your Area?
              </h3>
              <p className="text-muted-foreground mb-6">
                We're constantly expanding our service coverage. Enter your ZIP code during booking 
                to check availability, or give us a call to inquire about service in your area.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-foreground">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Same-day and next-day appointments
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  15+ service locations
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Professional technicians
                </li>
              </ul>
            </div>
            <div className="text-center lg:text-right">
              <div className="inline-block bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-muted-foreground mb-2">Check Your ZIP Code</p>
                <Link to="/book">
                  <Button
                    data-testid="areas-book-btn"
                    size="lg"
                    className="bg-accent hover:bg-accent/90 text-white font-semibold px-8 rounded-full mb-4"
                  >
                    Book Online
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <p className="text-muted-foreground mb-2">Or call us</p>
                <a
                  href="tel:+18556056034"
                  data-testid="areas-phone"
                  className="flex items-center justify-center gap-2 text-xl font-bold text-primary"
                >
                  <Phone className="w-5 h-5" />
                  (855) 605-6034
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ServiceAreasPage;
